/**
 * Area Profile API — backend calls for the V2 area profile page.
 *
 * READ (apiFetch):
 *   getareaprofile              (area basic info + residents + groups)
 *   getareadevices              (devices assigned to area)
 *   sp_dev_getInovonicsDevices  (all available devices for assignment)
 *   att_area_type               (area type dropdown)
 *   core_building               (building dropdown)
 *   getarealogs                 (area activity audit log)
 *   getcareflowwindows          (careflow plans)
 *   getwellnesswindows          (rounds windows)
 *   core_preform_event_notes    (preformatted note options)
 *   getlimitedevents            (recent events by area)
 *
 * READ (apiReport):
 *   dashboardhourchart          (hourly alarm chart)
 *   dashboardweekchart          (weekly trending chart)
 *   eventReasonSummary          (event reason breakdown)
 *
 * WRITE (apiTransaction):
 *   area_edit_profile           (edit area basic info)
 *   device_unassign_area        (remove device from area)
 *   device_assign_area          (add device to area)
 *
 * WRITE (apiSave):
 *   core_event_notes PUT        (add event notes)
 */

import { apiFetch, apiTransaction, apiSave, apiReport } from '../../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ — Basic area data (600s polling cycle)
// ---------------------------------------------------------------------------

/** Fetch full area profile by campus + area ID. */
export function fetchAreaProfile(campusId, areaId) {
  return apiFetch({
    procedure: 'getareaprofile',
    reqType: 'stored',
    payload: { area_id: areaId, campus_id: campusId },
    readOnly: true,
  });
}

export function fetchExternalAreaData(campusId, areaIds, vendorids) {
  return apiFetch({
    procedure: 'getexternalareas',
    reqType: 'stored',
    payload: { campus_id: campusId, area_ids: areaIds, vendor_ids: vendorids },
    readOnly: true,
  });
}

/** Fetch area type options. */
export function fetchAreaTypes(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_area_type',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/** Fetch buildings for the campus. */
export function fetchBuildings(campusId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_building',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/** Fetch devices assigned to this area. */
export function fetchAreaDevices(campusId, areaId) {
  return apiFetch({
    procedure: 'getareadevices',
    reqType: 'stored',
    payload: { area_id: areaId, campus_id: campusId },
    readOnly: true,
  });
}

/** Fetch all available devices for assignment. */
export function fetchAvailableDevices(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getInovonicsDevices',
    reqType: 'stored',
    payload: {
      device_filter: '',
      device_category_filter: '1,3,4,5,6,8,9,10,11,12,15,16,17',
      campus_id: campusId,
    },
    readOnly: true,
  });
}

/** Fetch area activity logs. */
export function fetchAreaLogs(areaId) {
  return apiFetch({
    procedure: 'getarealogs',
    reqType: 'stored',
    pKey: 'area_id',
    pVal: areaId,
    readOnly: true,
  });
}

/** Fetch careflow windows for the campus. */
export function fetchCareflowWindows(campusId) {
  return apiFetch({
    procedure: 'getcareflowwindows',
    reqType: 'stored',
    payload: {
      campus_id: campusId,
      care_types: '1,5',
      care_event_types: '1',
    },
    readOnly: true,
  });
}

/** Fetch rounds/wellness windows for the campus. */
export function fetchRoundsWindows(campusId) {
  return apiFetch({
    procedure: 'getwellnesswindows',
    reqType: 'stored',
    payload: {
      campus_id: campusId,
      window_types: '2',
    },
    readOnly: true,
  });
}

/** Fetch preformatted event note options. */
export function fetchPreformNotes(campusId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_preform_event_notes',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// READ — Reporting data (1200s polling cycle)
// ---------------------------------------------------------------------------

/** Fetch hourly alarm chart data for an area. */
export function fetchHourChart(campusId, areaId) {
  return apiReport({
    repType: 'dashboardhourchart',
    campusId,
    filters: { areaFilter: areaId.toString() },
  });
}

/** Fetch weekly trending chart data for an area. */
export function fetchWeekChart(campusId, areaId) {
  return apiReport({
    repType: 'dashboardweekchart',
    campusId,
    filters: { areaFilter: areaId.toString() },
  });
}

/** Fetch event reason summary for an area within a date range. */
export function fetchReasonSummary(campusId, areaId, startDate, endDate) {
  return apiReport({
    repType: 'eventReasonSummary',
    campusId,
    startDate,
    endDate,
    filters: { areaFilter: areaId.toString() },
  });
}

/** Fetch limited recent events for an area. */
export function fetchLimitedEvents(campusId, areaId) {
  return apiFetch({
    procedure: 'getlimitedevents',
    reqType: 'stored',
    payload: { campus_id: campusId, area_id: areaId.toString() },
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE — Area editing
// ---------------------------------------------------------------------------

/**
 * Edit area basic info.
 *
 * V1 TransType: area_edit_profile
 *
 * @param {Array}  payload    — [{ area_id, area_name, area_type_id, max_occupancy, building_id, modified_by, campus_id, vendor_ids }]
 * @param {Array}  oldPayload — [{ area_id, area_name, area_type_id, max_occupancy }]
 * @param {object} session    — { campusId, userId, clientId }
 */
export function editAreaProfile(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'area_edit_profile',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Remove (unassign) a device from an area.
 *
 * V1 TransType: device_unassign_area
 */
export function removeDeviceFromArea(device, areaId, session, grandstream = false) {
  const payload = [{
    device_id: device.device_id,
    device_name: device.device_name,
    manufacture_id: device.manufacture_id,
    manufacture_uuid: device.manufacture_uuid,
    sip_extension: device.sip_extension,
    is_grandstream: grandstream,
    is_active: false,
    area_id: null,
    modified_by: session.userId,
    vendor_ids: session.vendor_ids,
  }];

  const oldPayload = [{
    device_id: device.device_id,
    area_id: areaId,
  }];

  return apiTransaction({
    transType: 'device_unassign_area',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Assign an existing device to an area.
 *
 * V1 TransType: device_assign_area
 */
export function assignDeviceToArea(device, areaId, areaName, session, grandstream) {
  const payload = [{
    device_id: device.device_id,
    campus_id: session.campusId,
    is_active: true,
    area_id: parseInt(areaId),
    area_name: areaName,
    device_name: device.device_name,
    sip_extension: device.sip_extension,
    manufacture_model_id: parseInt(device.manufacture_model_id),
    manufacture_id: device.manufacture_id,
    device_use_id: parseInt(device.device_use_id || 0),
    descriptions: device.descriptions || '',
    is_grandstream: grandstream,
    is_resident: false,
    is_area_device: true,
    device_type_id: device.device_type_id,
    modified_by: session.userId,
    vendor_ids: session.vendor_ids,
  }];

  const oldPayload = [{
    device_id: device.device_id,
    campus_id: device.campus_id,
    is_active: device.is_active,
    area_id: device.area_id,
    device_use_id: device.device_use_id,
    device_name: device.device_name,
    descriptions: device.descriptions,
    manufacture_model_id: device.manufacture_model_id,
    device_type_id: device.device_type_id,
  }];

  return apiTransaction({
    transType: 'device_assign_area',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Add a note to an event.
 *
 * @param {Array}  noteItems — [{ event_id, campus_id, note, modified_by }]
 * @param {object} session   — { campusId, userId, clientId }
 */
export function addEventNote(noteItems, session) {
  return apiSave({
    table: 'core_event_notes',
    payload: noteItems,
    pKey: 'note_id',
    condition: 'primary',
    campusId: session.campusId,
    clientId: session.clientId,
    userId: session.userId,
    method: 'PUT',
  });
}
